import React, { useEffect } from 'react';
import {
  Icon,
  InfoMessage,
  Message,
  PageSubtitle,
  PageTitle,
  RequiredFieldsNotice
} from '@fasstech/middle-design-system';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer.jsx';
import {
  FormLayout,
  PageLayout
} from '@@viewCommonComponents/forms/Layouts.jsx';
import { FormProvider, useForm } from 'react-hook-form';
import useSouscriptionInformationsForm from './hooks/useSouscriptionInformationsForm.js';
import { and, isEmpty, not, propOr } from 'ramda';
import {
  isRequiredBooleanTrue,
  isRequiredInput,
  landlinePhoneNumberRegex,
  mobilePhoneNumberRegex
} from '@@viewHelpers/validations';
import {
  ControlledCity,
  ControlledInput,
  ControlledPhoneInput,
  ControlledPostalCode,
  ControlledResidenceCountry
} from '@@viewCommonComponents/controlled/index.js';
import useFetchCitiesQuery from './hooks/useFetchCitiesQuery.js';
import useFetchCountriesQuery from './hooks/useFetchCountriesQuery.js';
import useResidenceCountry from '@@viewCommonHooks/useResidenceCountry.mjs';
import {
  DATA_STEPS,
  adherentFieldFlags
} from '@@viewHelpers/adherentFieldFlags.js';
import { pickPhoneIndicatives } from '@@viewHelpers/residenceCountry.js';
import hasExtraUnrequiredFields from './extra/hasExtraUnrequiredFields.js';
import useTelEmailValidation from '@@viewCommonHooks/useTelEmailValidation.js';
import ControlledCheckbox from '@@viewCommonComponents/controlled/ControlledCheckbox.jsx';
import * as EmailValidator from 'email-validator';
import useMailMobileLandlineCrossValidation from '../../_common/client/hooks/useMobileLandlineCrossValidation.mjs';
import getExtraAdherentConsentementTelText from '../../simulationCustomerGrantees/client/extra/getExtraAdherentConsentementTelText.js';
import { isNotNilOrEmpty } from '@@serverHelpers';
import SouscriptionInformationsExtraFormRepresentative from './extra/SouscriptionInformationsExtraFormRepresentative.jsx';
import { getZipCodeRules } from '@@viewHelpers/zipCode';
import { RESIDENCE_COUNTRY_FIELD } from '@@viewHelpers/residenceCountry.js';

const SouscriptionInformationsForm = ({
  getMiddleText,
  getMiddleBoolean,
  middleFeatures = {},
  phoneIndicatives = [],
  internationalPhoneIndicatives = [],
  data
}) => {
  const { onPrevious, onSubmit, formatCustomer } =
    useSouscriptionInformationsForm();

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: formatCustomer(data)
  });

  const { isValid } = form.formState;

  useEffect(() => {
    const timeout = setTimeout(() => {
      form.trigger();
    }, 0);
    return () => clearTimeout(timeout);
  }, []);

  const emailConsent = form.watch('customer.GDPR.email');
  const phoneConsent = form.watch('customer.GDPR.phone');
  useEffect(() => {
    form.trigger('customer.GDPR.email');
    form.trigger('customer.GDPR.phone');
  }, [emailConsent, phoneConsent]);

  const mobileNumber = form.watch('customer.phones.0.number');
  const landlineNumber = form.watch('customer.phones.1.number');
  const aPhoneNumberHasAValueAndIsValid =
    (!isEmpty(mobileNumber) && mobilePhoneNumberRegex.test(mobileNumber)) ||
    (!isEmpty(landlineNumber) && landlinePhoneNumberRegex.test(landlineNumber));
  const email = form.watch('customer.emails.0.value');
  const emailHasAValueAndIsValid =
    !isEmpty(email) && EmailValidator.validate(email);

  const postalCode = form.watch('customer.addresses.0.zipCode');

  const {
    country,
    displayResidenceCountry,
    displayFrenchAddressFields,
    displayForeignAddressFields,
    displayPhoneFields,
    useFrenchPhoneRules
  } = useResidenceCountry(form, {
    getMiddleBoolean,
    dataStepKeys: [
      'ADHERENT_PAYS_RESIDENCE_EST_DEMANDE_DONNEES_CONTRAT',
      'ADHERENT_PAYS_RESIDENCE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
    ]
  });

  const fieldFlags = adherentFieldFlags(
    getMiddleBoolean,
    DATA_STEPS.SUBSCRIPTION
  );
  const residenceCountryFlags = fieldFlags('ADHERENT_PAYS_RESIDENCE');
  const postalCodeFlags = fieldFlags('ADHERENT_CODE_POSTAL');
  const cityFlags = fieldFlags('ADHERENT_VILLE');
  const { ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES = [] } = middleFeatures;
  const postalCodeFranceMetro = getZipCodeRules(
    'France Métropolitaine',
    ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES
  );
  const postalCodeDROM = getZipCodeRules(
    'DROM',
    ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES
  );
  const cfeContributionsPaidByRepresentative = propOr(
    false,
    'cfeContributionsPaidByRepresentative',
    data
  );

  const { fetchCountries: countries } = useFetchCountriesQuery({
    variables: { search: '' },
    skip:
      !displayResidenceCountry && !cfeContributionsPaidByRepresentative
  });

  // only for france
  const shouldSkipFetchCities =
    !getMiddleBoolean('ADHERENT_VILLE_EST_NECESSAIRE') ||
    !displayFrenchAddressFields;

  const { fetchCities: cities } = useFetchCitiesQuery({
    variables: {
      postalCode
    },
    skip: shouldSkipFetchCities
  });

  const phoneIndicativeOptions = pickPhoneIndicatives(country, {
    french: phoneIndicatives,
    international: internationalPhoneIndicatives
  });

  const landlineIndicativeOptions = displayResidenceCountry
    ? phoneIndicativeOptions
    : [];

  const { telEmailValidationHasError, telEmailValidationErrorMessage } =
    useTelEmailValidation(form, getMiddleBoolean);

  const getMailMobileLandlineInputRules = useMailMobileLandlineCrossValidation(
    form,
    getMiddleBoolean,
    useFrenchPhoneRules
  );

  const displayCommercialConsent =
    getMiddleBoolean('ADHERENT_CONSENTEMENT_COMMERCIAL_EST_NECESSAIRE') &&
    !getMiddleBoolean(
      'ADHERENT_CONSENTEMENT_COMMERCIAL_EST_DEMANDE_DONNEES_CONTRAT'
    ) &&
    getMiddleBoolean(
      'ADHERENT_CONSENTEMENT_COMMERCIAL_EST_DEMANDE_DONNEES_SOUSCRIPTION'
    );

  return (
    <FormProvider {...form}>
      <form className={'w-full'} onSubmit={form.handleSubmit(onSubmit)}>
        <PageLayout>
          <Icon name={'houseRounded'} size={70} />
          <PageTitle>
            {getMiddleText('DONNEES_SOUSCRIPTION_COORD_VIEW_TITLE')}
          </PageTitle>
          <PageSubtitle>
            {getMiddleText('DONNEES_SOUSCRIPTION_COORD_VIEW_DESCRIPTION')}
          </PageSubtitle>
          <RequiredFieldsNotice />

          <FormLayout>
            <ControlledInput
              name="customer.addresses.0.fields.0"
              label={getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP1')}
              form={form}
              rules={isRequiredInput(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP1_EST_OBLIGATOIRE')
              )}
              disabled={and(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP1_EST_OBLIGATOIRE'),
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT'
                )
              )}
              data-testid="ADHERENT_ADRESSE_CHAMP1"
              isDisplayed={
                getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
                getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                )
              }
            />
            <ControlledInput
              name="customer.addresses.0.fields.1"
              label={getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP2')}
              form={form}
              rules={isRequiredInput(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP2_EST_OBLIGATOIRE')
              )}
              disabled={and(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP2_EST_OBLIGATOIRE'),
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT'
                )
              )}
              data-testid="ADHERENT_ADRESSE_CHAMP2"
              isDisplayed={
                getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
                getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                )
              }
            />
            <ControlledInput
              name="customer.addresses.0.fields.2"
              label={getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP3')}
              form={form}
              containerClassName="col-span-2"
              rules={isRequiredInput(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP3_EST_OBLIGATOIRE')
              )}
              disabled={and(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP3_EST_OBLIGATOIRE'),
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT'
                )
              )}
              data-testid="ADHERENT_ADRESSE_CHAMP3"
              isDisplayed={
                getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
                getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                )
              }
            />
            <ControlledResidenceCountry
              form={form}
              name={RESIDENCE_COUNTRY_FIELD}
              label={getMiddleText('ADHERENT_PAYS_RESIDENCE_LABEL')}
              getMiddleText={getMiddleText}
              countries={propOr([], 'countries', countries)}
              isDisplayed={displayResidenceCountry}
              isRequired={residenceCountryFlags.isRequired}
              disabled={residenceCountryFlags.disabled}
              dataTestId="ADHERENT_PAYS_RESIDENCE"
            />
            <ControlledPostalCode
              form={form}
              name="customer.addresses.0.zipCode"
              postalCodeLabel={getMiddleText('ADHERENT_CODE_POSTAL_LABEL')}
              internationalZipCodeLabel={getMiddleText('ADHERENT_CODE_ZIP_LABEL')}
              isDisplayed={postalCodeFlags.isDisplayed}
              isRequired={postalCodeFlags.isRequired}
              disabled={postalCodeFlags.disabled}
              displayFrenchFields={displayFrenchAddressFields}
              displayForeignFields={displayForeignAddressFields}
              postalCodeFranceMetro={postalCodeFranceMetro}
              postalCodeDROM={postalCodeDROM}
              postalCodeTestId = 'ADHERENT_CODE_POSTAL'
              internationalZipCodeTestId = 'ADHERENT_CODE_ZIP'
            />
            {/* La ville est aussi conditionnée au code postal : couplage pré-existant. */}
            <ControlledCity
              form={form}
              name="customer.addresses.0.city"
              label={getMiddleText('ADHERENT_VILLE_LABEL')}
              cities={propOr([], 'cities', cities)}
              isDisplayed={postalCodeFlags.isNecessary && cityFlags.isDisplayed}
              isRequired={cityFlags.isRequired}
              disabled={cityFlags.disabled}
              displayFrenchFields={displayFrenchAddressFields}
              displayForeignFields={displayForeignAddressFields}
              cityTestId="ADHERENT_VILLE"
              foreignCityTestId="ADHERENT_VILLE_LIBRE"
            />
            <ControlledPhoneInput
              name={'customer.phones.0'}
              label={getMiddleText('ADHERENT_TEL_MOBILE_LABEL')}
              form={form}
              rules={getMailMobileLandlineInputRules('mobile')}
              options={phoneIndicativeOptions}
              disabled={
                (getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_OBLIGATOIRE') ||
                  getMiddleBoolean('ADHERENT_TEL_OU_FIXE_EST_OBLIGATOIRE') ||
                  getMiddleBoolean('ADHERENT_TEL_OU_EMAIL_EST_OBLIGATOIRE')) &&
                !isEmpty(mobileNumber) &&
                getMiddleBoolean(
                  'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT'
                )
              }
              data-testid="ADHERENT_TEL_MOBILE"
              isDisplayed={
                getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  )) &&
                displayPhoneFields
              }
            />
            <InfoMessage
              iconName={'lightbulb'}
              isDisplayed={
                getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  )) &&
                getMiddleBoolean('ADHERENT_TEL_MOBILE_NOTE_EST_AFFICHEE')
              }
              variant="info"
              className={'w-full'}
            >
              {getMiddleText('ADHERENT_TEL_MOBILE_NOTE_LABEL')}
            </InfoMessage>
            <ControlledPhoneInput
              name={'customer.phones.1'}
              isMobile={false}
              label={getMiddleText('ADHERENT_TEL_FIXE_LABEL')}
              form={form}
              rules={getMailMobileLandlineInputRules('landline')}
              options={landlineIndicativeOptions}
              disabled={
                (getMiddleBoolean('ADHERENT_TEL_FIXE_EST_OBLIGATOIRE') ||
                  getMiddleBoolean('ADHERENT_TEL_OU_FIXE_EST_OBLIGATOIRE')) &&
                !isEmpty(landlineNumber) &&
                getMiddleBoolean(
                  'ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT'
                )
              }
              data-testid="ADHERENT_TEL_FIXE"
              messageClassName="!mb-0"
              isDisplayed={
                getMiddleBoolean('ADHERENT_TEL_FIXE_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  )) &&
                displayPhoneFields
              }
            />
            <ControlledCheckbox
              isDisplayed={
                aPhoneNumberHasAValueAndIsValid &&
                getMiddleBoolean('ADHERENT_CONSENTEMENT_TEL_EST_NECESSAIRE') &&
                (getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE') ||
                  getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE')) &&
                (getMiddleBoolean(
                  'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  ))
              }
              containerClassName="mt-[-20px] mb-4"
              labelClassNames="pl-4"
              name={'customer.GDPR.phone'}
              label={getExtraAdherentConsentementTelText(
                getMiddleText('ADHERENT_CONSENTEMENT_TEL_TEXT')
              )}
              form={form}
              rules={isRequiredBooleanTrue(
                (getMiddleBoolean(
                  'ADHERENT_CONSENTEMENT_TEL_EST_OBLIGATOIRE'
                ) &&
                  !getMiddleBoolean(
                    'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
                  )) ||
                  (getMiddleBoolean(
                    'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
                  ) &&
                    !emailConsent)
              )}
            />
            <ControlledInput
              name="customer.emails.0.value"
              label={getMiddleText('ADHERENT_EMAIL_LABEL')}
              form={form}
              displayErrorMessage={true}
              rules={getMailMobileLandlineInputRules('email')}
              disabled={
                (getMiddleBoolean('ADHERENT_EMAIL_EST_OBLIGATOIRE') ||
                  getMiddleBoolean('ADHERENT_TEL_OU_EMAIL_EST_OBLIGATOIRE')) &&
                !isEmpty(email) &&
                getMiddleBoolean('ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT')
              }
              data-testid="ADHERENT_EMAIL"
              messageClassName="!mb-0"
              isDisplayed={
                getMiddleBoolean('ADHERENT_EMAIL_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  ))
              }
            />
            <InfoMessage
              iconName={'lightbulb'}
              isDisplayed={
                getMiddleBoolean('ADHERENT_EMAIL_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  )) &&
                getMiddleBoolean('ADHERENT_EMAIL_NOTE_EST_AFFICHEE')
              }
              variant="info"
              className={'w-full'}
            >
              {getMiddleText('ADHERENT_EMAIL_NOTE_LABEL')}
            </InfoMessage>
            <ControlledCheckbox
              isDisplayed={
                emailHasAValueAndIsValid &&
                getMiddleBoolean(
                  'ADHERENT_CONSENTEMENT_EMAIL_EST_NECESSAIRE'
                ) &&
                getMiddleBoolean('ADHERENT_EMAIL_EST_NECESSAIRE') &&
                getMiddleBoolean(
                  'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                )
              }
              containerClassName="mt-[-20px] mb-4"
              labelClassNames="pl-4"
              name={'customer.GDPR.email'}
              label={getMiddleText('ADHERENT_CONSENTEMENT_EMAIL_TEXT')}
              form={form}
              rules={isRequiredBooleanTrue(
                (getMiddleBoolean(
                  'ADHERENT_CONSENTEMENT_EMAIL_EST_OBLIGATOIRE'
                ) &&
                  !getMiddleBoolean(
                    'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
                  )) ||
                  (getMiddleBoolean(
                    'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
                  ) &&
                    !phoneConsent)
              )}
            />
            <Message
              className="w-full"
              variant={'error'}
              label={telEmailValidationErrorMessage}
            />
            <ControlledCheckbox
              isDisplayed={
                displayCommercialConsent &&
                isNotNilOrEmpty(
                  getMiddleText('ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION1')
                )
              }
              labelClassNames="pl-4"
              name={'customer.GDPR.company'}
              label={getMiddleText(
                'ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION1',
                { companyName: getMiddleText('GENERAL_CLIENT_NOM') }
              )}
              form={form}
            />
            <ControlledCheckbox
              isDisplayed={
                displayCommercialConsent &&
                isNotNilOrEmpty(
                  getMiddleText('ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION2')
                )
              }
              labelClassNames="pl-4"
              name={'customer.GDPR.partners'}
              label={getMiddleText(
                'ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION2',
                { companyName: getMiddleText('GENERAL_CLIENT_NOM') }
              )}
              form={form}
            />
            <SouscriptionInformationsExtraFormRepresentative
              form={form}
              cfeContributionsPaidByRepresentative={
                cfeContributionsPaidByRepresentative
              }
              countries={propOr([], 'countries', countries)}
              phoneIndicatives={phoneIndicatives}
              internationalPhoneIndicatives={internationalPhoneIndicatives}
              postalCodeFranceMetro={postalCodeFranceMetro}
              postalCodeDROM={postalCodeDROM}
            />
          </FormLayout>
        </PageLayout>

        <ButtonContainer
          $onPreviousButtonClick={onPrevious}
          nextButtonLabel={getMiddleText(
            'GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL'
          )}
          displayResumeButton
          nextButtonIsDisabled={
            not(isValid) ||
            !hasExtraUnrequiredFields(form) ||
            telEmailValidationHasError
          }
          isFormError={
            not(isValid) ||
            !hasExtraUnrequiredFields(form) ||
            telEmailValidationHasError
          }
          getMiddleText={getMiddleText}
        />
      </form>
    </FormProvider>
  );
};

export default SouscriptionInformationsForm;
